<template>
  <div class="ios-wheel" :style="wheelStyle">
    <div class="ios-wheel__highlight" :style="highlightStyle"></div>
    <div class="ios-wheel__columns">
      <div
        v-for="col in columns"
        :key="col.key"
        class="ios-wheel__col"
        :ref="'col-' + col.key"
        :style="{ flex: col.flex || 1 }"
        @scroll.passive="onScroll(col.key, $event)"
      >
        <div class="ios-wheel__spacer" :style="spacerStyle"></div>
        <div
          v-for="(item, i) in col.items"
          :key="item.value"
          class="ios-wheel__item"
          :class="{
            'is-selected': i === centeredIndex(col.key),
            'is-disabled': item.disabled
          }"
          :style="itemStyle"
          @click="onItemClick(col, i)"
        >
          <span class="ios-wheel__label">{{ item.label }}</span>
          <span
            v-if="item.dot"
            class="ios-wheel__dot"
            :style="{ backgroundColor: item.dot }"
          ></span>
        </div>
        <div class="ios-wheel__spacer" :style="spacerStyle"></div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Rueda de selección estilo iOS.
 *
 * Cada columna se define como { key, items: [{ value, label, disabled, dot }], flex }
 * y el valor actual se pasa como un objeto { [key]: value }.
 * Emite 'change' con { key, value } cuando la rueda se detiene sobre un elemento.
 */
export default {
  name: 'IosWheelPicker',

  props: {
    columns: {
      type: Array,
      default: () => []
    },
    value: {
      type: Object,
      default: () => ({})
    },
    itemHeight: {
      type: Number,
      default: 36
    },
    visibleCount: {
      type: Number,
      default: 5
    }
  },

  data () {
    return {
      centered: {},
      lock: {},
      rafs: {},
      timers: {}
    }
  },

  computed: {
    wheelStyle () {
      return { height: this.itemHeight * this.visibleCount + 'px' }
    },
    spacerStyle () {
      return { height: (this.itemHeight * (this.visibleCount - 1)) / 2 + 'px' }
    },
    itemStyle () {
      return { height: this.itemHeight + 'px', lineHeight: this.itemHeight + 'px' }
    },
    highlightStyle () {
      return {
        top: (this.itemHeight * (this.visibleCount - 1)) / 2 + 'px',
        height: this.itemHeight + 'px'
      }
    }
  },

  watch: {
    value: {
      deep: true,
      handler () {
        this.$nextTick(this.sync)
      }
    },
    columns: {
      deep: true,
      handler () {
        this.$nextTick(this.sync)
      }
    }
  },

  mounted () {
    this.$nextTick(this.sync)
  },

  beforeDestroy () {
    Object.keys(this.timers).forEach(key => clearTimeout(this.timers[key]))
    Object.keys(this.rafs).forEach(key => {
      if (this.rafs[key]) cancelAnimationFrame(this.rafs[key])
    })
  },

  methods: {
    centeredIndex (key) {
      return this.centered[key]
    },

    columnEl (key) {
      const ref = this.$refs['col-' + key]
      if (!ref) return null
      return Array.isArray(ref) ? ref[0] : ref
    },

    indexOfValue (col) {
      const current = this.value ? this.value[col.key] : undefined
      const idx = col.items.findIndex(item => item.value === current)
      return idx === -1 ? 0 : idx
    },

    /**
     * Coloca cada columna sobre su valor actual sin emitir eventos.
     * Debe llamarse cuando el picker se hace visible (dentro de un menú, tab, etc.).
     * Reintenta mientras las columnas no tengan altura (menú aún en transición).
     */
    sync (attempt) {
      const tries = attempt || 0
      let pending = false

      this.columns.forEach(col => {
        const el = this.columnEl(col.key)
        if (!el) {
          pending = true
          return
        }
        const idx = this.indexOfValue(col)
        this.$set(this.centered, col.key, idx)
        const top = idx * this.itemHeight
        if (Math.abs(el.scrollTop - top) < 1) return
        this.scrollToIndex(col.key, idx, false)
        // Si el contenedor aún no tiene alto, el scroll no se aplica
        if (Math.abs(el.scrollTop - top) >= 1) pending = true
      })

      if (pending && tries < 12) {
        clearTimeout(this.timers.__sync)
        this.timers.__sync = setTimeout(() => this.sync(tries + 1), 50)
      }
    },

    scrollToIndex (key, index, smooth) {
      const el = this.columnEl(key)
      if (!el) return
      this.$set(this.lock, key, true)
      clearTimeout(this.timers[key])
      const top = index * this.itemHeight
      if (smooth && typeof el.scrollTo === 'function') {
        el.scrollTo({ top: top, behavior: 'smooth' })
      } else {
        el.scrollTop = top
      }
      this.$set(this.centered, key, index)
      clearTimeout(this.timers['unlock-' + key])
      this.timers['unlock-' + key] = setTimeout(() => {
        this.$set(this.lock, key, false)
      }, smooth ? 400 : 80)
    },

    onScroll (key, event) {
      const el = event.target
      if (!this.rafs[key]) {
        this.rafs[key] = requestAnimationFrame(() => {
          this.rafs[key] = null
          const idx = Math.round(el.scrollTop / this.itemHeight)
          if (this.centered[key] !== idx) this.$set(this.centered, key, idx)
        })
      }
      if (this.lock[key]) return
      clearTimeout(this.timers[key])
      this.timers[key] = setTimeout(() => this.commit(key), 160)
    },

    commit (key) {
      const col = this.columns.find(c => c.key === key)
      const el = this.columnEl(key)
      if (!col || !el || !col.items.length) return

      let idx = Math.round(el.scrollTop / this.itemHeight)
      idx = Math.max(0, Math.min(col.items.length - 1, idx))

      const target = this.nearestEnabled(col.items, idx)
      if (target === -1) return

      if (target !== idx) {
        this.scrollToIndex(key, target, true)
      } else {
        this.$set(this.centered, key, idx)
      }

      const item = col.items[target]
      if (!this.value || this.value[key] !== item.value) {
        this.$emit('change', { key: key, value: item.value })
      }
    },

    nearestEnabled (items, idx) {
      if (items[idx] && !items[idx].disabled) return idx
      for (let step = 1; step < items.length; step++) {
        const before = idx - step
        const after = idx + step
        if (before >= 0 && items[before] && !items[before].disabled) return before
        if (after < items.length && items[after] && !items[after].disabled) return after
      }
      return -1
    },

    onItemClick (col, index) {
      if (col.items[index].disabled) return
      this.scrollToIndex(col.key, index, true)
      this.$emit('change', { key: col.key, value: col.items[index].value })
    }
  }
}
</script>

<style scoped>
.ios-wheel {
  position: relative;
  width: 100%;
  overflow: hidden;
  user-select: none;
}

.ios-wheel__columns {
  display: flex;
  height: 100%;
  align-items: stretch;
}

.ios-wheel__highlight {
  position: absolute;
  left: 6px;
  right: 6px;
  border-radius: 10px;
  background-color: rgba(120, 120, 128, 0.12);
  pointer-events: none;
  z-index: 0;
}

.ios-wheel__col {
  position: relative;
  height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
  scroll-snap-type: y mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  z-index: 1;
  -webkit-mask-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.35) 12%,
    rgba(0, 0, 0, 1) 38%,
    rgba(0, 0, 0, 1) 62%,
    rgba(0, 0, 0, 0.35) 88%,
    rgba(0, 0, 0, 0) 100%
  );
  mask-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.35) 12%,
    rgba(0, 0, 0, 1) 38%,
    rgba(0, 0, 0, 1) 62%,
    rgba(0, 0, 0, 0.35) 88%,
    rgba(0, 0, 0, 0) 100%
  );
}

.ios-wheel__col::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.ios-wheel__spacer {
  flex: none;
  scroll-snap-align: none;
}

.ios-wheel__item {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  scroll-snap-align: center;
  font-size: 20px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.45);
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s ease, font-weight 0.15s ease;
}

.ios-wheel__item.is-selected {
  color: rgba(0, 0, 0, 0.87);
  font-weight: 500;
}

.ios-wheel__item.is-disabled {
  color: rgba(0, 0, 0, 0.16);
  cursor: default;
}

.ios-wheel__label {
  line-height: 1;
}

.ios-wheel__dot {
  position: absolute;
  bottom: 5px;
  left: 50%;
  transform: translateX(-50%);
  width: 5px;
  height: 5px;
  border-radius: 50%;
}

.theme--dark .ios-wheel__item {
  color: rgba(255, 255, 255, 0.45);
}

.theme--dark .ios-wheel__item.is-selected {
  color: #fff;
}

.theme--dark .ios-wheel__item.is-disabled {
  color: rgba(255, 255, 255, 0.18);
}

.theme--dark .ios-wheel__highlight {
  background-color: rgba(255, 255, 255, 0.14);
}

@media (max-width: 600px) {
  .ios-wheel__item {
    font-size: 22px;
  }
}
</style>
